import * as React from 'react';
import {useMemo} from 'react';

import useRecommendations from '../../items/item-spinner/use-recommendations';
import {gaLegacyCustomEvent} from '../../../client/ga/ga-legacy.functions';
import {GetRecoItemsParams} from '../../monetate/monetate.types';
import {ItemSpinner} from '../../items/item-spinner/ItemSpinner';
import {MonetateService} from '../../../client/monetate/monetate.service';
import {pushGaEvent} from '../../../client/ga/ga.functions';
import {SearchResult} from '../search.types';
import {UsersService} from '../../../client/users/users.service';
import {User} from '../../users/user.class';
import {useService} from '../../react/ServiceContext';
import Button from '../../ui/Buttons/Button';

interface NullResultsProps {
    resultList?: SearchResult;
    user: User;
}

export const NullResults = ({resultList, user}: NullResultsProps) => {
    const componentName = `NullResults`;
    const monetateService: MonetateService = useService(`monetateService`);
    const usersService: UsersService = useService(`usersService`);
    const getRecoItemsParams: GetRecoItemsParams = useMemo(
        () => ({
            scheme: `nosearch1_rr`,
        }),
        [],
    );
    const recommendations = useRecommendations(getRecoItemsParams, monetateService);

    /**
     * TBD
     */
    const swapCatalog = () => {
        gaLegacyCustomEvent({eventAction: `null_search_custom_catalog_button`, eventCategory: `Search Field`});
        usersService
            .selectCatalog(user.defaultCatalogId)
            .then(() => {
                location.reload();
            })
            .catch(() => {
                // Error silently
            });
    };

    /**
     * Template
     */
    return (
        <div>
            <div>
                {user.hasCustomCatalogs() && !resultList.SearchString && (
                    <div className="tw-text-xl tw-font-normal tw-mb-2">
                        {user.isCatalogRestricted()
                            ? `Please consider adding more items from this category to your catalog.`
                            : `Please try switching to another catalog or consider adding more items from this category
                to your catalog.`}
                    </div>
                )}
                {user.activeCatalogId && user.activeCatalogId !== user.defaultCatalogId && (
                    <Button
                        className="tw-mb-4"
                        onClick={swapCatalog}
                        size="lg"
                        variant="secondary"
                    >
                        {resultList.SearchString ? `Search ` : `Browse `} Catalog: {user.defaultCatalogName}
                    </Button>
                )}
                {resultList.SearchString && (
                    <div className="tw-grid md:tw-flex tw-mb-4">
                        <div className="tw-w-full md:tw-w-6/12 lg:tw-w-7/12">
                            <div className="tw-h-full tw-flex">
                                <div className="tw-w-6/12 lg:tw-w-4/12 tw-mb-3 tw-pr-2.5 tw-h-full">
                                    <div className="tw-relative tw-flex tw-flex-col tw-min-w-0 tw-rounded-sm tw-border tw-bg-white tw-h-full">
                                        <div className="tw-p-4 tw-flex tw-items-center tw-h-full">
                                            <div>
                                                <div className="tw-font-bold">Use Exact Phrase</div>
                                                <div className="tw-text-sm tw-font-normal tw-leading-[1.3] tw-whitespace-normal">
                                                    Enter an item/model number or brand
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="tw-w-6/12 lg:tw-w-4/12 tw-mb-3 tw-pl-2.5 tw-pr-0 md:tw-px-2.5 tw-h-full">
                                    <div className="tw-relative tw-flex tw-flex-col tw-min-w-0 tw-rounded-sm tw-border tw-bg-white tw-h-full">
                                        <div className="tw-p-4 tw-flex tw-items-center tw-h-full">
                                            <div>
                                                <div className="tw-font-bold">Check Spelling</div>
                                                <div className="tw-text-sm tw-font-normal tw-leading-[1.3] tw-whitespace-normal">
                                                    Even a small typo can make a big difference
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                <div className="tw-w-4/12 tw-hidden lg:tw-block tw-px-2.5 tw-h-full">
                                    <div className="tw-relative tw-flex tw-flex-col tw-min-w-0 tw-rounded-sm tw-border tw-bg-white tw-h-full">
                                        <div className="tw-p-4 tw-flex tw-items-center tw-h-full">
                                            <div>
                                                <div className="tw-font-bold">Try Different Words</div>
                                                <div className="tw-text-sm tw-font-normal tw-leading-[1.3] tw-whitespace-normal">
                                                    You will get different results when searching 'glove' vs 'nitrile gloves'
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="tw-w-full md:tw-w-6/12 lg:tw-w-5/12 tw-pl-0 md:tw-pl-2.5 tw-mt-4 md:tw-mt-0">
                            <div className="tw-flex tw-w-full">
                                <div className="tw-hidden xl:tw-block tw-w-1/3" />
                                <div className="tw-relative tw-flex tw-flex-col tw-bg-white tw-border tw-w-full xl:tw-w-2/3">
                                    <div className="tw-p-4 tw-flex tw-flex-col">
                                        <div className="tw-font-bold">We can help you find it.</div>
                                        <div className="tw-mb-3 tw-text-sm tw-font-normal tw-leading-[1.3]">
                                            We are available M-F, 6 AM to 6 PM CST <br />
                                            Call{' '}
                                            <a
                                                href="tel:19005582808"
                                                onClick={() => {
                                                    gaLegacyCustomEvent({
                                                        eventAction: `null_search_phone_number_click`,
                                                        eventCategory: `search_field`,
                                                    });
                                                    pushGaEvent(`ga4_search_results_null_action`, {
                                                        click_type: `click_to_call`,
                                                        search_term: resultList.SearchString,
                                                    });
                                                }}
                                            >
                                                1-800-558-2808
                                            </a>
                                        </div>
                                        <Button
                                            block
                                            className="tw-mb-2 !tw-py-1.5 !tw-leading-normal !tw-h-fit"
                                            onClick={() => {
                                                gaLegacyCustomEvent({
                                                    eventAction: `null_search_start_chat`,
                                                    eventCategory: `Search Field`,
                                                });
                                                pushGaEvent(`ga4_search_results_null_action`, {
                                                    click_type: `start_chat`,
                                                    search_term: resultList.SearchString,
                                                });
                                                (window as any)?.LC_API?.open_chat_window();
                                            }}
                                            size="sm"
                                            variant="secondary"
                                        >
                                            Start Chat
                                        </Button>
                                        <Button
                                            block
                                            className="!tw-py-1.5 !tw-leading-normal !tw-h-fit"
                                            href="/contact-us"
                                            onClick={() => {
                                                gaLegacyCustomEvent({
                                                    eventAction: `null_search_send_email`,
                                                    eventCategory: `Search Field`,
                                                });
                                                pushGaEvent(`ga4_search_results_null_action`, {
                                                    click_type: `send_email`,
                                                    search_term: resultList.SearchString,
                                                });
                                            }}
                                            size="sm"
                                            variant="secondary"
                                        >
                                            Send Email
                                        </Button>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {recommendations?.items?.length > 0 && (
                <ItemSpinner
                    componentName={componentName}
                    gaItemListName={`scheme_${recommendations.schemeName}`}
                    label={recommendations.schemeExplanation}
                    loaded={recommendations.loaded}
                    maxSlides={7}
                    orderableItems={recommendations.items}
                    user={user}
                />
            )}
        </div>
    );
};
